/**
 * Supabase Realtime authentication manager (Task 106).
 *
 * Background
 * ----------
 * The Console used to subscribe to Supabase Realtime with the bare `anon`
 * key. Realtime evaluates row-level security as the role on the connection
 * JWT, and the `realtime_messages_select` / `realtime_sessions_select`
 * policies were `anon` + `USING(true)` — so any holder of the public anon
 * key could subscribe to *any* tenant's message/session change stream
 * (Task 106 SPEC: LIVE cross-tenant exposure, HIGH).
 *
 * Option B closes the hole: migration 082 swaps those policies to
 * `authenticated`-role + `tenant_id`-scoped. For the Console's Realtime
 * subscriptions to keep working, the WebSocket must now carry an
 * `authenticated` JWT with the operator's `tenant_id` claim.
 *
 * This module owns that JWT's lifecycle:
 *
 * - `startRealtimeAuth(tenantId)` — fetch a short-lived Supabase Realtime
 *   token from control-api (`POST /api/control/v1/realtime/token`) and
 *   apply it via `supabase.realtime.setAuth(token)`; schedule a refresh
 *   ahead of expiry.
 * - `stopRealtimeAuth()` — on sign-out / org switch, clear the token. The
 *   connection falls back to the anon key, which — post-migration-082 —
 *   sees zero rows. Safe by construction.
 * - `isRealtimeAuthReady()` — synchronous gate for the Realtime hooks:
 *   they bail out (no subscription) until an authenticated token is live,
 *   so a subscription is never opened on the (now powerless) anon key.
 *
 * The token is fetched cookie-authenticated (Scalekit session) — see
 * `apiClient`, which sends `credentials: 'include'` + `X-Tenant-ID`.
 */

import { apiClient } from './api-client';
import { getSupabase } from './supabase';

/** Shape returned by `POST /api/control/v1/realtime/token`. */
interface RealtimeTokenResponse {
  token: string;
  expires_at: string;
  tenant_id: string;
}

/**
 * Refresh the token this many milliseconds before it actually expires, so
 * the WebSocket never carries a stale JWT.
 */
const REFRESH_SKEW_MS = 60_000;

/** Floor for the refresh timer — never schedule a near-zero refresh loop. */
const MIN_REFRESH_DELAY_MS = 5_000;

/** Module-level lifecycle state. Single connection per page. */
let _ready = false;
let _activeTenantId: string | null = null;
let _refreshTimer: ReturnType<typeof setTimeout> | null = null;
/** Bumped on every start/stop so a stale in-flight fetch can detect it. */
let _generation = 0;

/** Optional subscribers notified when readiness flips (hooks re-run). */
type ReadyListener = (ready: boolean) => void;
const _listeners = new Set<ReadyListener>();

function _notify(ready: boolean): void {
  _ready = ready;
  for (const listener of _listeners) {
    try {
      listener(ready);
    } catch (err) {
      console.warn('[realtimeAuth] ready listener threw:', err);
    }
  }
}

/**
 * Whether an authenticated Realtime token is currently applied. Realtime
 * hooks call this synchronously and skip subscribing while it is false.
 */
export function isRealtimeAuthReady(): boolean {
  return _ready;
}

/**
 * Subscribe to readiness changes. Returns an unsubscribe function.
 * Used by the `useRealtimeAuthReady` hook so components re-render and
 * (re)open their subscriptions once the token lands.
 */
export function onRealtimeAuthReadyChange(listener: ReadyListener): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/** Clear any pending refresh timer. */
function _clearRefreshTimer(): void {
  if (_refreshTimer) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

/**
 * Fetch a fresh Realtime token from control-api and apply it.
 * `generation` guards against a stale fetch resolving after a stop/switch.
 */
async function _fetchAndApply(tenantId: string, generation: number): Promise<void> {
  let resp: { data: RealtimeTokenResponse };
  try {
    resp = await apiClient.post<RealtimeTokenResponse>('/control/v1/realtime/token');
  } catch (err) {
    // A failed mint must NOT silently leave a stale/anon token live —
    // surface it and leave Realtime un-ready (hooks stay bailed out).
    console.error('[realtimeAuth] failed to mint Realtime token:', err);
    if (generation === _generation) _notify(false);
    return;
  }

  // A stop() or org switch happened while the request was in flight.
  if (generation !== _generation) return;

  const { token, expires_at } = resp.data;

  let supabase;
  try {
    supabase = getSupabase();
  } catch (err) {
    console.error('[realtimeAuth] Supabase client unavailable:', err);
    _notify(false);
    return;
  }

  // Apply the authenticated JWT to the Realtime WebSocket. setAuth both
  // updates future joins and re-authorizes the live socket.
  supabase.realtime.setAuth(token);
  _notify(true);

  // Schedule the next refresh ahead of expiry.
  _clearRefreshTimer();
  const expiresAtMs = Date.parse(expires_at);
  const delay = Number.isFinite(expiresAtMs)
    ? Math.max(expiresAtMs - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS)
    : MIN_REFRESH_DELAY_MS;
  _refreshTimer = setTimeout(() => {
    void _fetchAndApply(tenantId, generation);
  }, delay);
}

/**
 * Start (or restart) Realtime authentication for the given tenant.
 *
 * Idempotent for the same tenant: calling it again with the tenant that
 * is already active is a no-op. Switching tenants tears down the previous
 * token first.
 *
 * @param tenantId - The operator's current organization id (== tenant_id).
 */
export function startRealtimeAuth(tenantId: string): void {
  if (!tenantId) return;
  if (_activeTenantId === tenantId && _ready) return;

  // New lifecycle generation — invalidates any in-flight fetch/refresh.
  _generation += 1;
  _clearRefreshTimer();
  _activeTenantId = tenantId;
  // Not ready until the token actually applies — hooks bail meanwhile.
  if (_ready) _notify(false);

  void _fetchAndApply(tenantId, _generation);
}

/**
 * Stop Realtime authentication — clears the token and the refresh timer.
 * Call on sign-out or before an org switch. After this, the Realtime
 * connection reverts to the anon key, which (post-migration-082) can read
 * nothing — fail-safe.
 */
export function stopRealtimeAuth(): void {
  _generation += 1;
  _clearRefreshTimer();
  _activeTenantId = null;

  try {
    // Revert the socket to the anon key (no tenant claim → 0 rows).
    getSupabase().realtime.setAuth(null);
  } catch {
    // Client not initialized yet — nothing to clear.
  }

  if (_ready) _notify(false);
}

/** Test-only: reset all module state. */
export function __resetRealtimeAuthForTests(): void {
  _generation += 1;
  _clearRefreshTimer();
  _activeTenantId = null;
  _ready = false;
  _listeners.clear();
}
